import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  Text,
  InlineStack,
  Banner,
  Tabs,
  Collapsible,
  Divider,
  Checkbox,
  DropZone,
  Thumbnail,
  useIndexResourceState,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../api';
import SegmentPicker from '../components/SegmentPicker';

const TRANSLATION_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pl', name: 'Polski' },
];

interface Campaign {
  id: string;
  title: string;
  body: string;
  segment: { type: string; value?: string | number };
  status: string;
  scheduledAt: string | null;
  sentAt: string | null;
  sentCount: number;
  showInNews: boolean;
  createdAt: string;
}

export default function Push() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [confirmSendId, setConfirmSendId] = useState<string | null>(null);

  // Form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState<{ type: string; value?: string | number }>({ type: 'all' });
  const [scheduledAt, setScheduledAt] = useState('');
  const [deepLink, setDeepLink] = useState('');

  // News section
  const [showInNews, setShowInNews] = useState(false);
  const [newsDurationDays, setNewsDurationDays] = useState('7');
  const [newsImageUrl, setNewsImageUrl] = useState('');

  // Translations
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const [translationTab, setTranslationTab] = useState(0);
  const [showTranslations, setShowTranslations] = useState(false);

  function updateTranslation(locale: string, field: string, value: string) {
    setTranslations((prev) => ({
      ...prev,
      [locale]: { ...(prev[locale] ?? {}), [field]: value },
    }));
  }

  const DEEP_LINK_OPTIONS = [
    { label: 'Ninguno', value: '' },
    { label: 'Novedades', value: 'news' },
    { label: 'Regalos', value: 'rewards' },
    { label: 'Pedidos', value: 'orders' },
    { label: 'Ritual', value: 'ritual' },
    { label: 'Tienda', value: 'shop' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<{ campaigns: Campaign[] }>('/panel-api/push/campaigns');
      setCampaigns(data.campaigns);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    try {
      const result = await apiPost<{ campaign: { id: string } }>('/panel-api/push/campaigns', {
        title,
        body,
        segment,
        data: deepLink ? { screen: deepLink } : undefined,
        scheduledAt: scheduledAt || undefined,
        showInNews,
        newsDurationDays: showInNews ? parseInt(newsDurationDays) || 7 : 7,
        newsImageUrl: showInNews ? newsImageUrl || undefined : undefined,
      });

      // Save translations
      const campaignId = result.campaign?.id;
      if (campaignId) {
        for (const [locale, fields] of Object.entries(translations)) {
          const hasValues = Object.values(fields).some((v) => v && v.trim() !== '');
          if (hasValues) {
            await apiPut(`/panel-api/translations/push_campaign/${campaignId}`, {
              locale,
              translations: fields,
            });
          }
        }
      }

      setShowModal(false);
      setTitle('');
      setBody('');
      setDeepLink('');
      setScheduledAt('');
      setShowInNews(false);
      setNewsDurationDays('7');
      setNewsImageUrl('');
      setTranslations({});
      setTranslationTab(0);
      setShowTranslations(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async (id: string) => {
    setSending(id);
    try {
      const result = await apiPost<{ ok: boolean; sentCount: number }>(
        `/panel-api/push/campaigns/${id}/send`,
      );
      setSuccess(`Campaña enviada a ${result.sentCount} dispositivos`);
      await fetchData();
    } catch (err) {
      console.error(err);
      setSuccess('Error al enviar la campaña');
    } finally {
      setSending(null);
      setConfirmSendId(null);
    }
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(campaigns as any);

  const rowMarkup = campaigns.map((c, idx) => (
    <IndexTable.Row
      id={c.id}
      key={c.id}
      position={idx}
      selected={selectedResources.includes(c.id)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">
          {c.title}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodySm" truncate>
          {c.body}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{c.segment?.type ?? 'all'}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Badge
            tone={
              c.status === 'sent'
                ? 'success'
                : c.status === 'scheduled'
                ? 'info'
                : undefined
            }
          >
            {c.status}
          </Badge>
          {c.showInNews && <Badge tone="attention">Novedades</Badge>}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{c.sentCount}</IndexTable.Cell>
      <IndexTable.Cell>
        {c.status === 'draft' && (
          <Button
            size="slim"
            variant="primary"
            onClick={() => setConfirmSendId(c.id)}
            loading={sending === c.id}
          >
            Enviar ahora
          </Button>
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Push Notifications"
      primaryAction={{ content: 'Create campaign', onAction: () => setShowModal(true) }}
    >
      {success && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="success" onDismiss={() => setSuccess('')}>
            <p>{success}</p>
          </Banner>
        </div>
      )}

      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: 'campaign', plural: 'campaigns' }}
              itemCount={campaigns.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: 'Title' },
                { title: 'Message' },
                { title: 'Segment' },
                { title: 'Status' },
                { title: 'Sent' },
                { title: 'Actions' },
              ]}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Create Push Campaign"
        primaryAction={{ content: 'Create', onAction: handleCreate }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setShowModal(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Title"
              value={title}
              onChange={setTitle}
              autoComplete="off"
            />
            <TextField
              label="Message body"
              value={body}
              onChange={setBody}
              multiline={3}
              autoComplete="off"
            />
            <SegmentPicker value={segment} onChange={setSegment} />
            <Select
              label="Al tocar, abrir..."
              options={DEEP_LINK_OPTIONS}
              value={deepLink}
              onChange={setDeepLink}
              helpText="Pantalla a la que navega el usuario al tocar la notificación"
            />
            <TextField
              label="Schedule (optional)"
              type="datetime-local"
              value={scheduledAt}
              onChange={setScheduledAt}
              autoComplete="off"
              helpText="Leave empty to save as draft"
            />

            <Divider />

            {/* News section */}
            <Checkbox
              label="Mostrar en Novedades"
              checked={showInNews}
              onChange={setShowInNews}
              helpText="Si está activado, esta campaña se mostrará en la sección Novedades de la app"
            />
            {showInNews && (
              <BlockStack gap="300">
                <TextField
                  label="Duración (días)"
                  type="number"
                  value={newsDurationDays}
                  onChange={setNewsDurationDays}
                  autoComplete="off"
                  helpText="Número de días que se mostrará en Novedades tras el envío (por defecto 7)"
                />
                <TextField
                  label="URL de imagen destacada"
                  value={newsImageUrl}
                  onChange={setNewsImageUrl}
                  autoComplete="off"
                  placeholder="https://cdn.shopify.com/..."
                  helpText="Imagen que se mostrará en la tarjeta de Novedades"
                />
                {newsImageUrl && (
                  <InlineStack gap="200" blockAlign="center">
                    <Thumbnail source={newsImageUrl} alt="Preview" size="large" />
                    <Text as="span" variant="bodySm" tone="subdued">Vista previa</Text>
                  </InlineStack>
                )}
              </BlockStack>
            )}

            <Divider />

            {/* Translations */}
            <Button
              onClick={() => setShowTranslations(!showTranslations)}
              disclosure={showTranslations ? 'up' : 'down'}
              variant="plain"
            >
              Traducciones ({String(TRANSLATION_LOCALES.length)} idiomas)
            </Button>
            <Collapsible open={showTranslations} id="push-translations">
              <BlockStack gap="300">
                <Text as="p" variant="bodySm" tone="subdued">
                  Traduce el título y cuerpo de la notificación. El idioma por defecto (Español) se configura arriba.
                </Text>
                <Tabs
                  tabs={TRANSLATION_LOCALES.map((l) => ({ id: l.code, content: l.name }))}
                  selected={translationTab}
                  onSelect={setTranslationTab}
                >
                  <Card>
                    <BlockStack gap="300">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Referencia (ES): {title || '(sin título)'} — {body || '(sin mensaje)'}
                      </Text>
                      <TextField
                        label={`Title (${TRANSLATION_LOCALES[translationTab].name})`}
                        value={translations[TRANSLATION_LOCALES[translationTab].code]?.title ?? ''}
                        onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, 'title', v)}
                        autoComplete="off"
                        placeholder={title}
                      />
                      <TextField
                        label={`Body (${TRANSLATION_LOCALES[translationTab].name})`}
                        value={translations[TRANSLATION_LOCALES[translationTab].code]?.body ?? ''}
                        onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, 'body', v)}
                        autoComplete="off"
                        multiline={3}
                        placeholder={body}
                      />
                    </BlockStack>
                  </Card>
                </Tabs>
              </BlockStack>
            </Collapsible>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Confirm send modal */}
      <Modal
        open={!!confirmSendId}
        onClose={() => setConfirmSendId(null)}
        title="Enviar campaña"
        primaryAction={{
          content: 'Enviar ahora',
          onAction: () => confirmSendId && handleSend(confirmSendId),
          loading: !!sending,
          destructive: false,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setConfirmSendId(null) }]}
      >
        <Modal.Section>
          <Text as="p">¿Enviar esta notificación push a todos los dispositivos del segmento?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
